/**
 * Anthos scent finder.
 * Loads every perfume from the anthos-finder-data feed (best sellers first), asks five
 * questions + an optional "a perfume you already love", scores each perfume in the browser
 * and shows the top three as quick-add cards (anthos-quick-add from anthos.js does the cart).
 */

const FAMILIES = [
  ['Ξυλώδες', 'Woody'], ['Λουλουδάτο', 'Floral'], ['Κιτρώδες', 'Citrus'], ['Βοτανικό', 'Herbal'],
  ['Φρουτώδες', 'Fruity'], ['Υδάτινο', 'Aquatic'], ['Τροπικό', 'Tropical'], ['Θαλασσινό', 'Marine'],
  ['Βρυώδες', 'Mossy'], ['Βαλσαμικό', 'Balsamic'], ['Μόσχος', 'Musk'], ['Άρωμα καθαριότητας', 'Clean'],
];
const GENDER_CLASS = { Γυναίκα: 'women', Women: 'women', Άνδρας: 'men', Men: 'men', Παιδί: 'kids', Kids: 'kids' };
const KIDS = ['Παιδί', 'Kids', 'Children', 'Child'];

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const list = (v) => (Array.isArray(v) ? v : v ? [v] : []);

class AnthosFinder {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('[data-finder-stage]');
    // Liquid's t filter HTML-escapes ("&" -> "&amp;"); decode once so esc() below doesn't double it
    const decode = (v) => {
      if (typeof v === 'string') return new DOMParser().parseFromString(v, 'text/html').documentElement.textContent;
      if (Array.isArray(v)) return v.map(decode);
      if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, decode(x)]));
      return v;
    };
    this.cfg = decode(JSON.parse(root.querySelector('[data-finder-config]').textContent));
    this.t = this.cfg.t;
    this.questions = this.cfg.questions;
    this.core = this.questions.filter((q) => !q.optional).length;
    this.answers = {};
    this.step = 0;
    this.products = null;
    this.ready = this.load();
    this.render();
  }

  async load() {
    const pages = [];
    const fetchPage = async (n) => {
      const res = await fetch(`${this.cfg.feed}&page=${n}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const script = doc.querySelector('script[data-finder-page]');
      return { items: JSON.parse(script.textContent), pages: Number(script.dataset.finderPages) || 1 };
    };
    const first = await fetchPage(1);
    pages.push(first.items);
    for (let n = 2; n <= first.pages; n++) pages.push((await fetchPage(n)).items);
    // best-selling order is the tie-breaker
    this.products = pages.flat().map((p, rank) => ({ ...p, rank, g: list(p.g), a: list(p.a), s: list(p.s), o: list(p.o) }));
    return this.products;
  }

  /* ---------- steps ---------- */

  render() {
    const q = this.questions[this.step];
    const progress = q.optional
      ? this.t.optional
      : this.t.step.replace('[current]', this.step + 1).replace('[total]', this.core);
    const pct = Math.round(((this.step + (q.optional ? 1 : 0)) / this.core) * 100);
    const picked = this.answers[q.id];

    let body = '';
    if (q.id === 'favourite') {
      body = `
        <div class="anthos-finder__fav">
          <input type="search" class="anthos-finder__input" placeholder="${esc(this.t.fav_placeholder)}" autocomplete="off" data-fav-input>
          <ul class="anthos-finder__suggest" role="listbox" data-fav-list></ul>
          ${this.answers.favourite ? `<p class="anthos-finder__fav-picked">✓ ${esc(this.answers.favourite.b || this.answers.favourite.t)}</p>` : ''}
        </div>`;
    } else {
      body = `
        ${q.multi ? `<p class="anthos-finder__hint">${esc(this.t.pick_two)}</p>` : ''}
        <div class="anthos-finder__options${q.options.length > 5 ? ' anthos-finder__options--many' : ''}">
          ${q.options
            .map((o) => {
              const on = q.multi ? (picked || []).includes(o.id) : picked === o.id;
              const dot = o.fam ? `<span class="anthos-finder__dot anthos-fam-${o.fam}"></span>` : '';
              return `<button type="button" class="anthos-finder__option" data-option="${o.id}" aria-pressed="${on}">${dot}<span>${esc(o.label)}</span></button>`;
            })
            .join('')}
        </div>`;
    }

    const canNext = q.multi ? (picked || []).length > 0 : q.optional ? true : !!picked;
    this.stage.innerHTML = `
      <div class="anthos-finder__card">
        <div class="anthos-finder__progress"><span style="width:${pct}%"></span></div>
        <p class="anthos-finder__count">${esc(progress)}</p>
        <h2 class="anthos-finder__q">${esc(q.title)}</h2>
        ${body}
        <div class="anthos-finder__nav">
          ${this.step > 0 ? `<button type="button" class="anthos-finder__back" data-back>← ${esc(this.t.back)}</button>` : '<span></span>'}
          ${
            q.optional
              ? `<div class="anthos-finder__nav-r"><button type="button" class="anthos-finder__skip" data-skip>${esc(this.t.skip)}</button><button type="button" class="anthos-finder__next" data-next>${esc(this.t.show)}</button></div>`
              : q.multi
                ? `<button type="button" class="anthos-finder__next" data-next ${canNext ? '' : 'disabled'}>${esc(this.t.next)} →</button>`
                : '<span></span>'
          }
        </div>
      </div>`;

    this.stage.querySelectorAll('[data-option]').forEach((b) => b.addEventListener('click', () => this.choose(q, b.dataset.option)));
    this.stage.querySelector('[data-back]')?.addEventListener('click', () => this.go(this.step - 1));
    this.stage.querySelector('[data-next]')?.addEventListener('click', () => this.next());
    this.stage.querySelector('[data-skip]')?.addEventListener('click', () => {
      delete this.answers.favourite;
      this.showResults();
    });
    if (q.id === 'favourite') this.bindFavourite();
  }

  go(step) {
    this.step = Math.max(0, step);
    this.render();
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  choose(q, id) {
    if (q.multi) {
      const cur = new Set(this.answers[q.id] || []);
      if (cur.has(id)) cur.delete(id);
      else {
        if (cur.size >= 2) cur.delete([...cur][0]);
        cur.add(id);
      }
      this.answers[q.id] = [...cur];
      this.render();
    } else {
      this.answers[q.id] = id;
      this.next();
    }
  }

  next() {
    if (this.step >= this.questions.length - 1) return this.showResults();
    this.go(this.step + 1);
  }

  bindFavourite() {
    const input = this.stage.querySelector('[data-fav-input]');
    const out = this.stage.querySelector('[data-fav-list]');
    const search = async () => {
      const q = norm(input.value.trim());
      if (q.length < 2) return (out.innerHTML = '');
      const products = this.products || (await this.ready);
      const seen = new Set();
      const hits = [];
      for (const p of products) {
        const label = p.b || '';
        if (!label || seen.has(label)) continue;
        if (norm(label).includes(q) || norm(p.t).includes(q)) {
          seen.add(label);
          hits.push(p);
          if (hits.length >= 6) break;
        }
      }
      out.innerHTML = hits.length
        ? hits.map((p, i) => `<li><button type="button" data-fav="${i}"><strong>${esc(p.b)}</strong><span>ANTHOS ${esc(p.t)}</span></button></li>`).join('')
        : `<li class="anthos-finder__suggest-none">${esc(this.t.fav_none)}</li>`;
      out.querySelectorAll('[data-fav]').forEach((b) =>
        b.addEventListener('click', () => {
          this.answers.favourite = hits[Number(b.dataset.fav)];
          this.showResults();
        }),
      );
    };
    input.addEventListener('input', search);
    input.focus({ preventScroll: true });
  }

  /* ---------- scoring ---------- */

  option(qid, id) {
    return this.questions.find((q) => q.id === qid)?.options.find((o) => o.id === id);
  }

  score(p) {
    const A = this.answers;
    let score = 0;
    let max = 0;
    const why = [];

    // gender: hard filter (unisex scents are fine for anyone, with a smaller bonus)
    if (p.g.some((g) => KIDS.includes(g))) return null;
    const gOpt = this.option('gender', A.gender);
    if (gOpt && gOpt.values.length) {
      max += 2;
      if (p.g.some((g) => gOpt.values.includes(g))) score += 2;
      else if (A.gender !== 'unisex' && p.g.includes('Unisex')) score += 0.5;
      else return null;
    }

    for (const id of A.family || []) {
      const o = this.option('family', id);
      max += 5;
      const hit = p.a.findIndex((a) => o.values.includes(a));
      if (hit >= 0) {
        score += hit < 2 ? 5 : 4;
        why.push(p.a[hit]);
      }
    }
    for (const id of A.tone || []) {
      const o = this.option('tone', id);
      max += 3;
      const hit = p.a.find((a) => o.values.includes(a));
      if (hit) {
        score += 3;
        if (!why.includes(hit)) why.push(hit);
      }
    }
    const occ = this.option('occasion', A.occasion);
    if (occ && occ.values.length) {
      max += 1;
      const hit = p.o.find((o) => occ.values.includes(o));
      if (hit) (score += 1), why.push(hit);
    }
    const sea = this.option('season', A.season);
    if (sea && sea.values.length) {
      max += 1;
      const hit = p.s.find((s) => sea.values.includes(s));
      if (hit) (score += 1), why.push(hit);
    }

    const fav = A.favourite;
    if (fav) {
      const shared = p.a.filter((a) => fav.a.includes(a)).length;
      const sim = fav.a.length ? shared / fav.a.length : 0;
      max += 4;
      score += sim * 4;
      if (p.b && p.b === fav.b) score += 100; // our version of the perfume they love
    }
    return { p, score, pct: max ? Math.min(99, Math.round((Math.min(score, max) / max) * 100)) : 0, why: why.slice(0, 3) };
  }

  async showResults() {
    this.stage.innerHTML = `<div class="anthos-finder__card anthos-finder__card--loading"><p>${esc(this.t.loading)}</p></div>`;
    const products = this.products || (await this.ready);
    const ranked = products
      .map((p) => this.score(p))
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || a.p.rank - b.p.rank);
    const top = ranked.slice(0, 3);
    const strong = ranked.filter((r) => r.pct >= 60).length;

    this.stage.innerHTML = `
      <div class="anthos-finder__results">
        <p class="anthos-eyebrow">${esc(this.t.results_eyebrow)}</p>
        <h2 class="anthos-finder__q">${esc(top.length ? this.t.results_title : this.t.results_none)}</h2>
        <div class="anthos-finder__grid">${top.map((r) => this.card(r)).join('')}</div>
        <div class="anthos-finder__after">
          ${strong > 3 ? `<a class="anthos-btn anthos-btn--ink" href="${esc(this.allUrl())}">${esc(this.t.see_all.replace('[count]', strong))}</a>` : ''}
          <button type="button" class="anthos-textlink anthos-finder__restart" data-restart>${esc(this.t.restart)}</button>
        </div>
      </div>`;
    this.stage.querySelector('[data-restart]').addEventListener('click', () => {
      this.answers = {};
      this.go(0);
    });
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  allUrl() {
    const url = new URL(this.cfg.collectionUrl, location.origin);
    const present = (values) => values.filter((v) => this.products.some((p) => p.g.includes(v) || p.a.includes(v)));
    const g = this.option('gender', this.answers.gender);
    if (g) present(g.values).forEach((v) => url.searchParams.append('filter.p.m.custom.gender', v));
    for (const id of this.answers.family || []) {
      present(this.option('family', id).values).forEach((v) => url.searchParams.append('filter.p.m.custom.accords', v));
    }
    return url.pathname + url.search;
  }

  card({ p, pct, why }) {
    const fam = p.a.map((a) => FAMILIES.findIndex((f) => f.includes(a))).find((i) => i >= 0);
    const famPill = fam !== undefined ? `<span class="anthos-tag-family anthos-fam-${fam + 1}">${esc(p.a.find((a) => FAMILIES[fam].includes(a)))}</span>` : '';
    const g = p.g.length ? `<span class="anthos-tag-gender anthos-gen-${GENDER_CLASS[p.g[0]] || 'unisex'}">${esc(p.g.join(' · '))}</span>` : '';
    const first = p.v.find((v) => v[0] === p.f) || p.v[0];
    const anyAvailable = p.v.some((v) => v[3]);
    const pills =
      p.v.length > 1
        ? `<fieldset class="anthos-pills">${p.v
            .map(
              ([id, title, price, ok]) =>
                `<label class="anthos-pill"><input type="radio" name="id" value="${id}" data-price="${esc(price)}" ${id === first[0] ? 'checked' : ''} ${ok ? '' : 'disabled'}><span>${esc(title)}</span></label>`,
            )
            .join('')}</fieldset>`
        : `<input type="hidden" name="id" value="${first[0]}">`;
    const buy = anyAvailable
      ? `<anthos-quick-add><form action="${window.Shopify?.routes?.root || '/'}cart/add" method="post" class="anthos-card__form">${pills}
           <button type="submit" class="anthos-card__add"><span class="anthos-card__add-label">${esc(this.t.add)} · <span data-anthos-price>${esc(first[2])}</span></span><span class="anthos-card__added-label">${esc(this.t.added)}</span></button>
         </form></anthos-quick-add>`
      : `<p class="anthos-card__soldout">${esc(this.t.sold_out)}</p>`;
    return `
      <article class="anthos-card anthos-finder__result">
        <a class="anthos-card__media" href="${esc(p.u)}">
          ${p.i ? `<img src="${esc(p.i)}" alt="" loading="lazy" width="600" height="750">` : ''}
          <span class="anthos-badge">${esc(this.t.match.replace('[percent]', pct))}</span>
          ${g}
        </a>
        <div class="anthos-card__head"><h3 class="anthos-card__title"><a href="${esc(p.u)}">${esc(p.t)}</a></h3>${famPill}</div>
        ${why.length ? `<p class="anthos-tag-tone">${esc(why.join(' · '))}</p>` : ''}
        ${p.b ? `<p class="anthos-finder__insp">${esc(this.t.inspired)} <strong>${esc(p.b)}</strong></p>` : ''}
        ${buy}
      </article>`;
  }
}

document.querySelectorAll('[data-anthos-finder]').forEach((el) => new AnthosFinder(el));
